import { TrackPackage } from "../components/site/TrackPackage";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, CheckCircle2, Circle, Package, Send, FileText, Truck, Wallet, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { usePageMeta } from "../lib/seo";
import { Container, ContactPills } from "../components/site/Reveal";

const STEPS = [
  ["new", "Request received", FileText],
  ["offer_sent", "Offer sent", Send],
  ["shipped", "Package on the way", Truck],
  ["received", "Package received", Package],
  ["paid", "Payment sent", Wallet],
];
const normalize = (s) => (s === "contacted" ? "offer_sent" : s === "completed" ? "paid" : s);
const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const Track = () => {
  const { openChat, t } = useSite();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  usePageMeta({ title: "Track Your Payout | StripWise", description: "Check the status of your StripWise quote: package received and payment sent.", path: "/track" });

  useEffect(() => { setErr(""); }, [code, contact]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setData(null);
    try {
      const r = await api.get("/track", { params: { code, contact } });
      setData(r.data);
    } catch (e2) {
      setErr(e2?.response?.status === 404 ? t("Not found") : e2?.response?.data?.detail || t("Not found"));
    } finally {
      setBusy(false);
    }
  };

  const cur = data ? STEPS.findIndex((s) => s[0] === normalize(data.status)) : -1;
  const when = (k) => {
    const h = data?.history?.filter((x) => normalize(x.status) === k).pop();
    return h ? fmt(h.at) : null;
  };
  const input = "w-full rounded-xl border border-[#dcebe8] bg-white px-4 py-3.5 text-[15.5px] outline-none focus:border-[#0e8c7f] focus:ring-2 focus:ring-[#0e8c7f]/15";

  return (
    <section className="bg-[linear-gradient(180deg,#eefbf8,#ffffff)] py-14 md:py-20" data-testid="track-page">
      <Container className="max-w-[760px] lg:w-[60%]">
        <div className="text-center sw-fade-up">
          <div className="sw-section-eyebrow">{t("Payout Tracker")}</div>
          <h1 className="mt-3 font-jakarta text-[36px] font-extrabold leading-tight tracking-[-0.03em] text-[#0f2e2a] md:text-[48px]">{t("Where's my payment?")}</h1>
          <p className="mt-3 text-[17px] text-[#4b5d5a]">{t("Enter your quote number (like")} <b>SW-12345</b>{t(") and the email or phone you used.")}</p>
        </div>
        <form onSubmit={submit} className="mt-8 grid gap-3 rounded-[24px] border border-[#e0f0ec] bg-white p-5 shadow-[0_30px_60px_-40px_rgba(15,46,42,.35)] sm:grid-cols-[1fr_1fr_auto]" data-testid="track-form">
          <input className={input} placeholder={t("Quote number")} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required data-testid="track-code-input" />
          <input className={input} placeholder={t("Email or phone")} value={contact} onChange={(e) => setContact(e.target.value)} required data-testid="track-contact-input" />
          <button disabled={busy} className="sw-btn-primary disabled:opacity-60" data-testid="track-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}{t("Track")}</button>
        </form>
        {err && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-[14.5px] text-red-700" data-testid="track-error">{err}</p>}

        {data && (
          <div className="mt-8 rounded-[24px] border border-[#e0f0ec] bg-white p-6 md:p-8 sw-fade-up" data-testid="track-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[13px] font-semibold uppercase tracking-wider text-[#6b7c79]">{t("Quote")} {data.code}</div>
                <div className="mt-1 text-[22px] font-bold text-[#0f2e2a]">{t("Hi {name}, status:", { name: data.name })} <span className="text-[#0e8c7f]" data-testid="track-status">{t(data.status_label)}</span></div>
              </div>
              {data.amount && <div className="rounded-2xl bg-[#e8f8f4] px-4 py-2.5 text-right"><div className="text-[12px] text-[#5b6d6a]">{t("Payout")}</div><div className="font-jakarta text-[22px] font-extrabold text-[#0b6f65]">{data.amount}</div>{data.method && <div className="text-[12px] text-[#5b6d6a]">{t("via")} {data.method}</div>}</div>}
            </div>
            <ol className="mt-7 space-y-0">
              {STEPS.map(([k, label, I], i) => {
                const done = i <= cur;
                return (
                  <li key={k} className="relative flex gap-4 pb-6 last:pb-0">
                    {i < STEPS.length - 1 && <span className={`absolute left-[19px] top-10 h-[calc(100%-36px)] w-0.5 ${i < cur ? "bg-[#0e8c7f]" : "bg-[#e3ecea]"}`} />}
                    <span className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${done ? "bg-[#0e8c7f] text-white" : "bg-[#f0f4f3] text-[#a3b3b0]"}`}><I size={17} /></span>
                    <div className="pt-1.5">
                      <div className={`flex items-center gap-2 font-semibold ${done ? "text-[#0f2e2a]" : "text-[#9aaba8]"}`}>{t(label)}{done ? <CheckCircle2 size={15} className="text-[#0e8c7f]" /> : <Circle size={13} />}</div>
                      <div className="text-[13px] text-[#6b7c79]">{when(k) || (done ? "" : t("Pending"))}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
            <button onClick={() => openChat(t("Hi! Question about my quote {code}.", { code: data.code }))} className="sw-btn-outline mt-7 text-[14.5px]"><MessageCircle size={15} />{t("Questions? Chat with us")}</button>
          </div>
        )}
        <TrackPackage />
        <ContactPills className="mt-10" />
      </Container>
    </section>
  );
};

export default Track;
